<template>
  <div
    class="selected-quote-container"
    :class="
      isAnnualFrequency
        ? 'selected-quote-container-annual'
        : 'selected-quote-container-monthly'
    "
  >
    <div class="selected-quote-header font-20">
      <span>Your Landlord’s Insurance quote</span>
    </div>
    <v-row :fluid="true" class="insurer-price">
      <v-col cols="12" lg="5" class="divide-right pa-0 px-lg-0 pr-lg-4">
        <div>
          <div class="monthly-price__wrapper">
            <div class="insurer-logo">
              <v-img
                :src="require(`@/assets/images/insurers/${insurer.image}.png`)"
                transition="scale-transition"
                :max-width="$vuetify.breakpoint.lgAndUp ? 124 : 86"
                :max-height="$vuetify.breakpoint.lgAndUp ? 124 : 86"
                contain
                :alt="insurer.name"
              />
            </div>
            <div class="insurer-monthly-price">
              <p class="mb-0 text-center">
                <span
                  v-if="isAnnualFrequency || showPayByCreditDebitCardMessage"
                  >Annual</span
                >
                <span v-else>Monthly</span>
              </p>
              <div>
                <span
                  v-if="paymentFrequency && !showPayByCreditDebitCardMessage"
                  class="monthly-price-amount"
                  :class="{
                    'monthly-price-amount--smaller':
                      insurer.selectedQuote &&
                      insurer.selectedQuote[`${paymentFrequency}Total`] >=
                        MIN_SMALLER_TEXT_PRICE,
                  }"
                  >{{
                    insurer.selectedQuote
                      ? insurer.selectedQuote[`${paymentFrequency}Total`]
                      : "" | formatCurrency
                  }}</span
                >
                <span
                  v-else
                  class="monthly-price-amount"
                  :class="{
                    'monthly-price-amount--smaller':
                      insurer.selectedQuote?.AnnualTotal >=
                      MIN_SMALLER_TEXT_PRICE,
                  }"
                  >{{
                    insurer.selectedQuote?.AnnualTotal | formatCurrency
                  }}</span
                >
              </div>
              <div class="view-breakdown-btn">
                <a @click.prevent="handleShowBreakdown">View a breakdown</a>
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="
            isPaymentPage &&
            ['Monthly', 'Monthly2MonthsFree'].includes(paymentFrequency)
          "
        >
          <div class="mt-4 mt-lg-9">
            <div class="d-flex align-start font-14">
              <span>
                You will pay <b>{{ totalPayable | formatCurrency }}</b> in total
                if you choose to pay monthly, which means that you pay
                <b>{{ creditCharge | formatCurrency }}</b> more than if you paid
                for your policy in full today. This is because there is an
                interest charge applied when paying monthly.
                <a
                  href="javascript:"
                  class="find-out-more-dialog"
                  @click="showModal('DialogContentQuoteMoreDetail')"
                  >Find out more</a
                >
              </span>
            </div>
          </div>

          <div
            class="mt-4 mb-n1"
            v-if="
              $vuetify.breakpoint.mdAndDown &&
              insurer.isAvailable &&
              insurer.onPanelUntil &&
              showWarningQuoteExpireMessage
            "
          >
            <v-row>
              <v-col cols="12" class="mb-0">
                <v-alert
                  class="cut-off-message mb-0 d-flex flex-grow-1"
                  color="var(--orange-100)"
                >
                  <div class="d-flex flex-no-wrap">
                    <div class="mr-2">
                      <v-icon color="var(--orange-500)">mdi-alert</v-icon>
                    </div>
                    <div>
                      <span class="font-weight-bold">Note:</span
                      >&nbsp;Quotations provided by {{ insurer.name }} will only
                      be valid until the {{ insurerEndDate(insurer) }}.
                    </div>
                  </div>
                </v-alert>
              </v-col>
            </v-row>
          </div>
        </div>
      </v-col>
      <v-col
        v-if="$vuetify.breakpoint.lgAndUp || isViewMoreMobile"
        cols="12"
        lg="7"
        class="mt-4 mt-lg-0 insurer-cover"
      >
        <v-row class="pl-lg-7">
          <v-col
            cols="12"
            v-if="isBuildingsCover"
            lg="6"
            class="pb-6 pt-lg-0 px-9 px-lg-5 pl-lg-0 insurance-section"
          >
            <div class="insurance-amount">
              <h3 class="insurance-heading mb-3">Buildings Insurance</h3>
              <div class="pl-6 pl-lg-0 mb-1">
                <b>£1,000,000</b>
                cover
              </div>
              <div class="pl-6 pl-lg-0 mb-1">
                <b>{{
                  cover.buildingsCover.excess
                    | formatCurrency(AMOUNT_INSURED_FORMAT)
                }}</b>
                excess
              </div>
              <div class="pl-6 pl-lg-0 mb-1">
                <b
                  >{{
                    quoteExcessConfiguration?.BuildingsWaterExcess
                      | formatCurrency(AMOUNT_INSURED_FORMAT)
                  }}
                  escape</b
                >
                of water excess
              </div>
              <div class="pl-6 pl-lg-0 mb-1">
                <b>{{
                  quoteExcessConfiguration?.BuildingsSubsidenceExcess
                    | formatCurrency(AMOUNT_INSURED_FORMAT)
                }}</b>
                subsidence excess
              </div>
            </div>
            <div class="accidential-damage mt-4">
              <div class="font-weight-bold mb-1">Accidental damage</div>
              <div class="d-flex pl-6 pl-lg-0">
                <v-icon
                  v-if="insurer.selectedQuote?.BuildingsAD"
                  class="icon darkgreen-icon"
                  >mdi-checkbox-marked-circle</v-icon
                >
                <v-icon v-else class="icon red-icon">mdi-close-circle</v-icon>
                <div
                  v-if="insurer.selectedQuote?.BuildingsAD"
                  class="ml-3 accidental-damage__text"
                >
                  Included
                </div>
                <div v-else class="ml-3 accidental-damage__text">
                  Not included
                </div>
              </div>
            </div>
          </v-col>
          <v-col
            cols="12"
            lg="6"
            v-if="isContentsCover"
            class="pb-6 pt-lg-0 px-9 px-lg-5 pr-lg-0 insurance-section divide-top d-flex flex-column"
          >
            <div class="insurance-amount">
              <h3 class="insurance-heading mb-3">Contents Insurance</h3>
              <div class="pl-6 pl-lg-0 mb-1">
                <b>{{
                  cover.contentsCover.amount
                    | formatCurrency(AMOUNT_INSURED_FORMAT)
                }}</b>
                cover
              </div>
              <div class="pl-6 pl-lg-0 mb-1">
                <b>{{
                  cover.contentsCover.excess
                    | formatCurrency(AMOUNT_INSURED_FORMAT)
                }}</b>
                excess
              </div>
              <div class="pl-6 pl-lg-0 mb-1">
                <b
                  >{{
                    quoteExcessConfiguration?.ContentWaterExcess
                      | formatCurrency(AMOUNT_INSURED_FORMAT)
                  }}
                  escape</b
                >
                of water excess
              </div>
            </div>
            <div class="accidential-damage mt-4 mt-md-auto">
              <div class="font-weight-bold mb-1">Accidental damage</div>
              <div class="d-flex pl-6 pl-lg-0">
                <v-icon
                  v-if="insurer.selectedQuote?.ContentsAD"
                  class="icon darkgreen-icon"
                  >mdi-checkbox-marked-circle</v-icon
                >
                <v-icon v-else class="icon red-icon">mdi-close-circle</v-icon>
                <div
                  v-if="insurer.selectedQuote?.ContentsAD"
                  class="ml-3 accidental-damage__text"
                >
                  Included
                </div>
                <div v-else class="ml-3 accidental-damage__text">
                  Not included
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-col>
      <v-col
        v-if="$vuetify.breakpoint.lgAndUp || isViewMoreMobile"
        cols="12"
        class="pt-2 pt-lg-5 pb-2 pb-lg-0 px-lg-0 px-9 insurance-section additional-cover-section"
      >
        <h3 class="insurance-heading pb-lg-2 additional-cover-heading">
          Additional cover
        </h3>
        <div
          class="py-4 d-flex flex-column flex-lg-row justify-space-start additional-cover-items"
        >
          <div
            class="cover d-flex font-weight-bold additional-cover-item mb-1 mb-lg-0"
          >
            <div class="icon-cover d-flex align-start pl-6 pl-lg-0 mr-1">
              <v-icon
                v-if="cover.landlordsEmergency"
                class="icon darkgreen-icon"
                >mdi-checkbox-marked-circle</v-icon
              >
              <v-icon v-else class="icon red-icon">mdi-close-circle</v-icon>
            </div>
            <div
              :class="[
                'cover-text',
                cover.landlordsEmergency
                  ? 'cover-text__green'
                  : 'cover-text__red',
              ]"
            >
              <div class="cover-title">Landlord’s emergency</div>
              <div v-if="cover.landlordsEmergency" class="cover-included">
                Included
              </div>
              <div v-else class="cover-included">Not included</div>
            </div>
          </div>
          <div
            class="cover d-flex font-weight-bold additional-cover-item mb-1 mb-lg-0"
          >
            <div class="icon-cover d-flex align-start pl-6 pl-lg-0 mr-1">
              <v-icon v-if="cover.legalExpenses" class="icon darkgreen-icon"
                >mdi-checkbox-marked-circle</v-icon
              >
              <v-icon v-else class="icon red-icon">mdi-close-circle</v-icon>
            </div>
            <div
              :class="[
                'cover-text',
                cover.legalExpenses ? 'cover-text__green' : 'cover-text__red',
              ]"
            >
              <div class="cover-title">Legal expenses</div>
              <div v-if="cover.legalExpenses" class="cover-included">
                Included
              </div>
              <div v-else class="cover-included">Not included</div>
            </div>
          </div>
          <div
            class="cover d-flex font-weight-bold additional-cover-item mb-1 mb-lg-0"
          >
            <div class="icon-cover d-flex align-start pl-6 pl-lg-0 mr-1">
              <v-icon v-if="cover.rentGuarantee" class="icon darkgreen-icon"
                >mdi-checkbox-marked-circle</v-icon
              >
              <v-icon v-else class="icon red-icon">mdi-close-circle</v-icon>
            </div>
            <div
              :class="[
                'cover-text',
                cover.rentGuarantee ? 'cover-text__green' : 'cover-text__red',
              ]"
            >
              <div class="cover-title">Rent protection</div>
              <div v-if="cover.rentGuarantee" class="cover-included">
                Included
              </div>
              <div v-else class="cover-included">Not included</div>
            </div>
          </div>
        </div>
      </v-col>
    </v-row>
    <div
      v-if="$vuetify.breakpoint.mdAndDown"
      :class="[!isViewMoreMobile ? '' : 'mt-4', 'view-more-contents__wrapper']"
    >
      <a
        v-if="isViewMoreMobile"
        @click="isViewMoreMobile = false"
        class="view-link view-less-link"
      >
        <span>View less information</span>
        <v-icon> mdi-chevron-up </v-icon>
      </a>
      <a
        v-else
        @click="isViewMoreMobile = true"
        class="view-link view-more-link"
      >
        <span>View more information</span>
        <v-icon> mdi-chevron-down </v-icon>
      </a>
    </div>
  </div>
</template>

<script>
import PremiumBreakdownMixin from "@/Mixins/PremiumBreakdownMixin";
import BuildingsContentsVisibility from "@/Mixins/QuoteResults/BuildingsContentsVisibility";
import { MIN_SMALLER_TEXT_PRICE } from "@/constants/ui-config.constants";
import { mapMutations, mapActions, mapState } from "vuex";
import {
  DIALOG_MODULE,
  QUESTIONS_MODULE,
  APPLICATION_MODULE,
} from "@/store/types/module-types";
import {
  DIRECTDEBIT,
  MONTHLY,
  SUBMITTED,
  ANNUAL,
} from "@/constants/common.constants";
import { UNSPECIFIED_PERSONAL_POSSESSIONS_D2C } from "@/constants/possessions-options";
import { AMOUNT_INSURED_FORMAT } from "@/constants/currency-format.constants";
export default {
  name: "TheSelectedQuote",
  props: {
    insurer: {
      type: Object,
      required: true,
    },
    cover: {
      type: Object,
      required: true,
    },
    paymentFrequency: {
      type: String,
    },
    isPaymentPage: {
      type: Boolean,
      default: false,
    },
    showPayByCreditDebitCardMessage: {
      type: Boolean,
      default: false,
    },
    quoteExcessConfiguration: {
      type: Object,
      required: false,
    },
  },
  mixins: [PremiumBreakdownMixin, BuildingsContentsVisibility],
  data() {
    return {
      MIN_SMALLER_TEXT_PRICE,
      AMOUNT_INSURED_FORMAT,
      SUBMITTED: SUBMITTED,
      isViewMoreMobile: false,
    };
  },
  methods: {
    ...mapMutations(DIALOG_MODULE, ["showModal"]),
    ...mapActions(QUESTIONS_MODULE, [
      "handleSaveAndExitOnResults",
      "handleD2CSaveAndExitOnResults",
      "setOverlayVisible",
      "handleSaveQuote",
    ]),
    handleShowBreakdown() {
      if (this.isPaymentPage || this.applyResponse.QuoteStatus === SUBMITTED) {
        this.$emit(
          "showD2CPremiumBreakdown",
          this.insurer.selectedQuote,
          DIRECTDEBIT,
          this.innerPaymentFrequency
        );
      } else {
        this.$emit(
          "showD2CPremiumBreakdown",
          this.insurer.selectedQuote,
          DIRECTDEBIT,
          MONTHLY
        );
      }
    },
  },
  computed: {
    ...mapState(APPLICATION_MODULE, ["applyResponse"]),
    isAnnualFrequency() {
      return !["Monthly", "Monthly2MonthsFree"].includes(this.paymentFrequency);
    },
    innerPaymentFrequency() {
      return this.paymentFrequency ? this.paymentFrequency : ANNUAL;
    },
    additionalCoverAmount() {
      if (!this.cover || !this.cover.unspecifiedPpAmount) {
        return 0;
      }
      const collection =
        this.cover.contentsCover?.amount >= this.getContentConfigD2C.max
          ? UNSPECIFIED_PERSONAL_POSSESSIONS_D2C.higherUnspecifiedAmounts
          : UNSPECIFIED_PERSONAL_POSSESSIONS_D2C.standardUnspecifiedAmounts;
      const value =
        collection.find(
          (amount) => amount.value === this.cover.unspecifiedPpAmount
        )?.numberValue ?? 0;

      return value;
    },
    breakdownModel() {
      return this.buildPremiumBreakdownModel(
        this.insurer.selectedQuote,
        DIRECTDEBIT,
        this.innerPaymentFrequency
      );
    },
    creditCharge() {
      return this.breakdownModel.premium?.CreditDetails?.CreditCharge;
    },
    totalPayable() {
      return this.breakdownModel.price?.Values?.TotalAmountPayable;
    },
  },
};
</script>

<style lang="scss" scoped>
.icon {
  font-size: 20px;

  &.darkgreen-icon {
    color: #1d8649;
  }

  &.red-icon {
    color: var(--red-500);
  }

  &.blue-icon {
    color: #5285c0;
  }
}

.cover-text {
  margin-left: 3px;

  &__green {
    .cover-included {
      color: #1d8649;
    }
  }

  &__red {
    .cover-included {
      color: var(--red-500);
    }
  }
}

.selected-quote-container {
  border-radius: 10px;
  color: #272727;
  .selected-quote-header {
    height: 48px;
    padding: 8px 25px;
    color: #ffffff;
    font-weight: 700;
    border-top-left-radius: 10px;
    border-top-right-radius: 10px;
    background-color: $black-3;
    border-bottom: none !important;
  }
  .insurer-price {
    max-width: 963px;
    margin: 0;
    .monthly-price__wrapper {
      display: flex;
      align-items: center;
      .monthly-price-amount {
        text-align: left;
        font-size: 26px;
        font-weight: bold;
        font-stretch: normal;
        font-style: normal;
        line-height: normal;
        letter-spacing: 0.75px;
      }
      .view-breakdown-btn {
        text-align: center;
        a {
          font-size: 16px;
          font-weight: normal;
          font-stretch: normal;
          font-style: normal;
          line-height: normal;
          letter-spacing: 0.3px;
          color: #106175 !important;
          text-decoration: underline;
        }
      }
      .no-break-word {
        display: inline-block;
        font-size: 18px;
      }
    }
    .annual-price__wrapper {
      margin-top: 15px;
      font-size: 16px;
      b {
        font-size: 18px !important;
      }
    }
    .insurance-heading {
      font-size: 20px;
    }
    .icon-cover {
      padding-top: 2px;
    }
    .find-out-more-dialog {
      color: #4d7fb8 !important;
      font-weight: 700;
    }
    .rounded {
      border-radius: 10px !important;
    }
    .additional-cover-items {
      .additional-cover-item {
        margin-right: 80px;
      }
    }
    .divide-top {
      border-top: 1px solid #7c869a;
    }
  }
  .view-more-contents__wrapper {
    text-align: center;
    margin: 32px 0px;
    a {
      gap: 8px;
      cursor: pointer;
      font-size: 20px;
      font-weight: bold;
      font-stretch: normal;
      font-style: normal;
      line-height: normal;
      letter-spacing: 0.4px;
      text-align: center;
      color: #106175;
      user-select: none;
      display: inline-flex;
      align-items: center;
      letter-spacing: 0;
      span {
        letter-spacing: 0.4px;
      }
      i {
        color: #106175;
        font-size: 20px;
      }
    }
  }
}

@media screen and (max-width: $grid-breakpoints-md) {
  .insurer-price {
    margin: 0px;
    .insurer-cover {
      border-top-left-radius: 10px;
      border-top-right-radius: 10px;
      border: 1px solid #7c869a;
      border-bottom: none !important;
    }
    .insurer-cover > div > div:first-child {
      border-top: none !important;
    }
    .insurance-section {
      border-color: #7c869a !important;
    }
    .additional-cover-section {
      border-bottom-left-radius: 10px;
      border-bottom-right-radius: 10px;
      border: 1px solid #7c869a;
    }
  }
  .selected-quote-header {
    border: 1px solid #7c869a;
  }
  .monthly-price__wrapper {
    border-radius: 0px 0px 10px 10px;
    border: 1px solid #7c869a;
    border-top: none !important;
    overflow: hidden;
  }
  .insurer-logo,
  .insurer-monthly-price {
    width: 50%;
    height: 160px;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
  }
  .insurer-logo {
    border-right: 1px solid #7c869a;
  }
  .insurer-monthly-price {
    background-color: $gray-light-5;
  }
}

@media screen and (min-width: $grid-breakpoints-md) {
  .divide-right {
    border-right: 1px solid #ddd;
  }
  .divide-top {
    border: none;
  }
  .selected-quote-container {
    border: 1px solid #ddd;
    .selected-quote-header {
      height: 48px;
    }
    .insurer-price {
      padding: 10px 14px;
      margin: 0;
      .monthly-price__wrapper {
        padding-left: 0px;
        padding-right: 53px;
        .monthly-price-amount {
          font-size: 28px;
          &.monthly-price-amount--smaller {
            font-size: 24px;
          }
        }
      }
      .annual-price__wrapper {
        padding-left: 7px !important;
      }
      .insurance-amount {
        min-height: 160px;
      }
      .insurance-section {
        border: none;
      }
      .additional-cover-heading {
        padding-left: 25px;
        border-bottom: 1px solid #ddd;
      }
      .insurer-monthly-price {
        padding-top: 30px;
      }
      .additional-cover-items {
        padding-left: 25px;
      }
    }
  }
}

@media screen and (min-width: $grid-breakpoints-lg) {
  .selected-quote-container {
    max-width: 964px;
    &-annual {
      height: 465px;
    }
    &-monthly {
      height: 487px;
    }
    .selected-quote-header {
      height: 48px;
      min-width: 963px;
    }
    .insurer-price {
      padding: 21px 25px;
      min-width: 963px;
      .insurer-logo {
        margin-right: 34px;
      }
      .additional-cover-section {
        margin-left: -25px;
        min-width: 963px;
      }
      .monthly-price-amount {
        font-size: 40px;
      }
      .monthly-price__wrapper {
        .monthly-price-amount {
          font-size: 48px;
          &.monthly-price-amount--smaller {
            font-size: 40px;
          }
        }
      }
    }
  }
}
</style>
